import React, { useState, useRef } from 'react';
import Layout from 'Component/Layouts/layout';
import {
  Box,
  TextField,
  FormLabel,
  FormHelperText,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Button,
  Typography,
  CircularProgress,
} from '@mui/material';
import StepperComponent from 'Component/Stepper';
// import { Textarea } from "@mui/joy";
import uploadImg from 'assets/uploadIcon.png';
import DeleteIcon from '@mui/icons-material/Delete';
import coursesTypeData from 'Constants/courseData';
import useApiCaller from 'hooks/apiCaller';
import { useNavigate } from 'react-router-dom';

const UploadCourse = () => {
  const fileInputRef = useRef();
  const fileInputSubtitleRef = useRef(null);
  const apiCaller = useApiCaller();
  const navigate = useNavigate();
  const [courseDetails, setCourseDetails] = useState({
    courseTitle: '',
    description: '',
    startDate: null,
    endDate: null,
    courseType: '',
    isMandatory: '',
    totalNumOfAttempts: null,
    passingScore: '33',
  });
  const [stepperCount, setStepperCount] = useState(0);
  const [uploadedCourses, setUpdatedCourses] = useState([]);
  const [questionnaire, setQuestionnaire] = useState([]);
  const [subtitles, setSubtitles] = useState([]);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Multi-language video support
  const [englishVideo, setEnglishVideo] = useState(null);
  const [englishSubtitle, setEnglishSubtitle] = useState(null);
  const [subtitleColor, setSubtitleColor] = useState('#ffffff');

  // Multi-language question support
  const [languageQuestions, setLanguageQuestions] = useState([]);

  const LANGUAGE_OPTIONS = [
    { code: 'as', name: 'Assamese' },
    { code: 'bn', name: 'Bengali' },
    { code: 'hi', name: 'Hindi' },
    { code: 'kn', name: 'Kannada' },
    { code: 'pa', name: 'Punjabi' },
    { code: 'te', name: 'Telugu' },
    { code: 'ta', name: 'Tamil' },
    { code: 'or', name: 'Oriya' },
    { code: 'mr', name: 'Marathi' },
    { code: 'gu', name: 'Gujarati' },
  ];

  const validateField = (field, value) => {
    let errorMsg = '';

    switch (field) {
      case 'courseTitle':
        if (!value) errorMsg = 'Course Title is required.';
        break;
      case 'startDate':
        if (!value) {
          errorMsg = 'Start Date is required.';
        } else if (courseDetails.endDate && new Date(value) > new Date(courseDetails.endDate)) {
          errorMsg = 'Start Date cannot be after End Date.';
        }
        break;
      case 'endDate':
        if (!value) {
          errorMsg = 'End Date is required.';
        } else if (courseDetails.startDate && new Date(value) < new Date(courseDetails.startDate)) {
          errorMsg = 'End Date cannot be before Start Date.';
        }
        break;
      case 'passingScore':
        if (value < 0 || value > 100) {
          errorMsg = 'Passing Score must be between 0 and 100.';
        }
        break;
      case 'courseType':
        if (!value) errorMsg = 'Course Type is required.';
        break;
      case 'totalNumOfAttempts':
        if (!value) errorMsg = 'No of Attempts is required.';
        break;
      case 'isMandatory':
        if (!value) errorMsg = 'Mandatory is required.';
        break;
      default:
        break;
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [field]: errorMsg,
    }));

    return !errorMsg;
  };

  const handleValidate = () => {
    const isValid = Object.keys(courseDetails).every((field) =>
      validateField(field, courseDetails[field])
    );
    return isValid;
  };

  const handleAddLanguageQuestion = () => {
    setLanguageQuestions([
      ...languageQuestions,
      { languageCode: '', languageName: '', file: null },
    ]);
  };

  const handleRemoveLanguageQuestion = (index) => {
    setLanguageQuestions(languageQuestions.filter((_, i) => i !== index));
  };

  const handleLanguageQuestionChange = (index, field, value) => {
    const updated = [...languageQuestions];
    updated[index][field] = value;
    if (field === 'languageCode') {
      const lang = LANGUAGE_OPTIONS.find((l) => l.code === value);
      updated[index].languageName = lang ? lang.name : '';
    }
    setLanguageQuestions(updated);
  };

  const getAvailableLanguagesForQuestion = (currentIndex) => {
    const selectedCodes = languageQuestions
      .map((lq, idx) => (idx !== currentIndex ? lq.languageCode : null))
      .filter(Boolean);
    return LANGUAGE_OPTIONS.filter((lang) => !selectedCodes.includes(lang.code));
  };

  const handleOnClickNext = () => {
    const isValid = handleValidate();

    // Validate video course uploads
    if (stepperCount === 1) {
      if (courseDetails.courseType === 'video') {
        if (!englishVideo) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            englishVideo: 'English video is required for video courses',
          }));
          return;
        }
        if (!englishSubtitle) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            englishSubtitle: 'English subtitle is required for video courses',
          }));
          return;
        }
      } else if (uploadedCourses.length === 0) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          uploadedCourses: 'Upload course',
        }));
        return;
      }
    }

    if (stepperCount === 2 && questionnaire.length === 0) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        questionnaire: 'Upload questionnaire file',
      }));
      return;
    }

    if (stepperCount === 2) {
      for (let i = 0; i < languageQuestions.length; i++) {
        if (!languageQuestions[i].languageCode || !languageQuestions[i].file) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            [`languageQuestion_${i}`]: 'Please select both language and question file',
          }));
          return;
        }
      }
    }

    if (isValid) {
      if (stepperCount < 2) {
        setStepperCount((prev) => prev + 1);
      }
    } else {
      console.log('Form has errors:', errors);
    }
  };

  const handleOnClickPrevious = () => {
    if (stepperCount > 0) {
      setStepperCount((prev) => prev - 1);
    }
  };
  const handleFileChange = (val) => {
    setUpdatedCourses([...uploadedCourses, val]);
    setErrors((prevErrors) => ({
      ...prevErrors,
      uploadedCourses: '',
    }));
  };

  const handleQuestionnaireUpload = (val) => {
    setQuestionnaire([...questionnaire, val]);
    setErrors((prevErrors) => ({
      ...prevErrors,
      questionnaire: '',
    }));
  };

  const handleOnChange = (key, name) => {
    setCourseDetails((prevCourseDetails) => ({
      ...prevCourseDetails,
      [key]: name,
    }));

    validateField(key, name);

    if (validateField(key, name)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [key]: '',
      }));
    }
  };

  // handleSubtitleChange
  const handleSubtitleChange = (val) => {
    setSubtitles([...subtitles, val]);
  };

  const handleDeleteItem = (vl) => {
    setUpdatedCourses(uploadedCourses.filter((item, index) => index !== vl));
  };
  const handleDeleteQuestionnaire = (vl) => {
    setQuestionnaire(questionnaire.filter((item, index) => index !== vl));
  };

  const handleDeleteSubtitle = (vl) => {
    setSubtitles(subtitles.filter((item, index) => index !== vl));
  };

  const handleSumbitUploadDetails = async () => {
    setIsLoading(true);
    const formData = new FormData();
    Object.keys(courseDetails).forEach((key) => {
      formData.append(key, courseDetails[key]);
    });

    // For video courses, use multi-language approach
    if (courseDetails.courseType === 'video') {
      if (englishVideo) {
        formData.append('courseUrl', englishVideo);
      }
      if (englishSubtitle) {
        formData.append('subtitles', englishSubtitle);
        formData.append('subtitleColor', subtitleColor);
      }
    } else {
      // For non-video courses, use legacy approach
      uploadedCourses.forEach((file) => {
        formData.append('courseUrl', file);
      });
      subtitles.forEach((file) => {
        formData.append('subtitles', file);
      });
    }

    questionnaire.forEach((file) => {
      formData.append('quesUrl', file);
    });

    // Append additional language question files
    languageQuestions.forEach((lq) => {
      if (lq.file && lq.languageCode) {
        formData.append(`questionContent[${lq.languageCode}]`, lq.file);
      }
    });

    try {
      const response = await apiCaller.post(`super-user/createCourse`, formData);
      if (response.data.status === 'success') {
        setIsLoading(false);
        navigate('/courses-mapping');
      }
    } catch (error) {
      setIsLoading(false);
      console.log(error);
    }
  };

  return (
    <Layout>
      <Box
        sx={{
          fontSize: '24px',
          fontWeight: 500,
          background: 'linear-gradient(to bottom, #E9FBE0 0%, #E6F0E8 100%)',
          position: 'relative',
        }}
        className="p-3"
      >
        Upload New Course
      </Box>
      <Box className="mt-3 w-50 mx-auto">
        {' '}
        <StepperComponent stepperCount={stepperCount} />
      </Box>
      {stepperCount === 0 && (
        <Box className="mx-auto" sx={{ width: '40%' }}>
          <Box className="mt-2">
            <TextField
              fullWidth
              label="Title of Courses"
              id="outlined-size-small"
              size="small"
              //   name="courseTitle"
              onChange={(e) => handleOnChange('courseTitle', e.target.value)}
              value={courseDetails.courseTitle}
              error={!!errors.courseTitle}
              helperText={errors.courseTitle}
            />
          </Box>
          <Box className="mt-2">
            <FormControl fullWidth>
              <FormLabel>Course Description</FormLabel>
              <TextField
                placeholder="Text"
                multiline
                rows={2}
                fullWidth
                onChange={(e) => handleOnChange('description', e.target.value)}
                value={courseDetails.description}
              />
              <FormHelperText sx={{ marginLeft: 0 }}>{errors.description}</FormHelperText>
            </FormControl>
          </Box>

          <Grid container spacing={1} columns={16} className="mt-1">
            <Grid item xs={8}>
              <FormControl fullWidth>
                <FormLabel>Course Start Date</FormLabel>
                <TextField
                  fullWidth
                  id="outlined-size-small"
                  size="small"
                  type="date"
                  onChange={(e) => handleOnChange('startDate', e.target.value)}
                  value={courseDetails.startDate}
                  error={!!errors.startDate}
                  helperText={errors.startDate}
                />
              </FormControl>
            </Grid>
            <Grid item xs={8}>
              <FormControl fullWidth>
                <FormLabel>Course End Date</FormLabel>
                <TextField
                  fullWidth
                  id="outlined-size-small"
                  size="small"
                  type="date"
                  onChange={(e) => handleOnChange('endDate', e.target.value)}
                  value={courseDetails.endDate}
                  error={!!errors.endDate}
                  helperText={errors.endDate}
                />
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={1} columns={16} className="mt-1">
            <Grid item xs={8}>
              <FormControl fullWidth error={!!errors.courseType}>
                <FormLabel>Course Type</FormLabel>
                <Select
                  size="small"
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  onChange={(e) => handleOnChange('courseType', e.target.value)}
                  value={courseDetails.courseType}
                >
                  {coursesTypeData.map((val) => {
                    return <MenuItem value={val}>{val}</MenuItem>;
                  })}
                </Select>
                <FormHelperText>{errors.courseType}</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={8}>
              <FormControl fullWidth error={!!errors.isMandatory}>
                <FormLabel>Is the Course Compulsory</FormLabel>
                <Select
                  size="small"
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  onChange={(e) => handleOnChange('isMandatory', e.target.value)}
                  value={courseDetails.isMandatory}
                >
                  <MenuItem value={true}>Yes</MenuItem>
                  <MenuItem value={false}>No</MenuItem>
                </Select>
                <FormHelperText>{errors.isMandatory}</FormHelperText>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={1} columns={16} className="mt-1">
            <Grid item xs={8}>
              <FormControl fullWidth error={!!errors.totalNumOfAttempts}>
                <FormLabel>Number of Attempts</FormLabel>

                <Select
                  size="small"
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  onChange={(e) => handleOnChange('totalNumOfAttempts', e.target.value)}
                  value={courseDetails.totalNumOfAttempts}
                >
                  <MenuItem value={1}>1</MenuItem>
                  <MenuItem value={2}>2</MenuItem>
                  <MenuItem value={3}>3</MenuItem>
                  <MenuItem value={4}>4</MenuItem>
                  <MenuItem value={5}>5</MenuItem>
                </Select>
                <FormHelperText>{errors.totalNumOfAttempts}</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={8}>
              <FormControl fullWidth>
                <FormLabel>Passing Score (%)</FormLabel>
                <TextField
                  size="small"
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  onChange={(e) => handleOnChange('passingScore', e.target.value)}
                  value={courseDetails.passingScore}
                  error={!!errors.passingScore}
                  helperText={errors.passingScore}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      )}

      {stepperCount === 1 && (
        <>
          {courseDetails.courseType === 'video' ? (
            <>
              {/* Multi-language video upload for video courses */}
              <Box sx={{ width: '40%', margin: 'auto' }} className="p-3">
                <Box sx={{ fontSize: 18, fontWeight: 600, marginBottom: 2, color: '#110B2C' }}>
                  English Video & Subtitle (Required)
                </Box>

                {/* English Video Upload */}
                <Box className="mt-2">
                  <FormLabel>English Video *</FormLabel>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    sx={{ marginTop: 1, textTransform: 'none' }}
                  >
                    {englishVideo ? englishVideo.name : 'Upload English Video'}
                    <input
                      type="file"
                      hidden
                      accept="video/*"
                      onChange={(e) => {
                        setEnglishVideo(e.target.files[0]);
                        setErrors((prev) => ({ ...prev, englishVideo: '' }));
                      }}
                    />
                  </Button>
                  {errors.englishVideo && (
                    <FormHelperText error>{errors.englishVideo}</FormHelperText>
                  )}
                </Box>

                {/* English Subtitle Upload */}
                <Box className="mt-3">
                  <FormLabel>English Subtitle *</FormLabel>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    sx={{ marginTop: 1, textTransform: 'none' }}
                  >
                    {englishSubtitle ? englishSubtitle.name : 'Upload English Subtitle (.vtt)'}
                    <input
                      type="file"
                      hidden
                      accept=".vtt"
                      onChange={(e) => {
                        setEnglishSubtitle(e.target.files[0]);
                        setErrors((prev) => ({ ...prev, englishSubtitle: '' }));
                      }}
                    />
                  </Button>
                  {errors.englishSubtitle && (
                    <FormHelperText error>{errors.englishSubtitle}</FormHelperText>
                  )}
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ display: 'block', marginTop: 1 }}
                  >
                    Subtitles will be auto-translated to all 10 Indian languages
                  </Typography>
                </Box>

                {/* Subtitle Color Picker */}
                <Box className="mt-3">
                  <FormLabel>Subtitle Color</FormLabel>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginTop: 1 }}>
                    <input
                      type="color"
                      value={subtitleColor}
                      onChange={(e) => setSubtitleColor(e.target.value)}
                      style={{
                        width: 48,
                        height: 40,
                        border: '1px solid #ccc',
                        borderRadius: 6,
                        cursor: 'pointer',
                        padding: 2,
                      }}
                    />
                    <Typography variant="body2" sx={{ color: '#475569' }}>
                      Choose the color for subtitle text (default: white)
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      mt: 1,
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      background: '#000',
                      display: 'inline-block',
                    }}
                  >
                    <Typography
                      sx={{ color: subtitleColor, fontSize: 13, fontFamily: 'monospace' }}
                    >
                      Preview subtitle text
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </>
          ) : (
            <>
              {/* Upload Course Details - Legacy for non-video courses */}
              <Box sx={{ width: '40%', margin: 'auto' }} className="p-3">
                <Box
                  sx={{
                    color: '#110B2C',
                    fontSize: 16,
                    fontWeight: 500,
                    marginBottom: '-12px',
                  }}
                >
                  Upload Course Content
                </Box>
                <Box
                  sx={{
                    height: '200px',
                    background: '#F5FFF1',
                    border: '1px solid #9F9DAA',
                    borderStyle: 'dashed',
                    cursor: 'pointer',
                    borderRadius: 2,
                  }}
                  className="mt-3 d-flex justify-content-center align-items-center"
                  onClick={(e) => fileInputRef.current.click()}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileChange(e.target.files[0])}
                  />
                  <Box>
                    <Box>
                      <img
                        src={uploadImg}
                        alt="upload-img"
                        style={{ margin: 'auto', display: 'block' }}
                      />
                      <Box
                        sx={{
                          fontSize: 16,
                          color: '#110B2C',
                          fontWeight: 600,
                        }}
                        className="mt-3 mx-2"
                      >
                        Drag & drop files or <u style={{ color: '#2800B1' }}>Browse</u>
                      </Box>
                      <Typography
                        sx={{ fontSize: 12, color: '#676767', fontWeight: 400 }}
                        className="mt-3"
                      >
                        Supported formats: PDF, Word, PPTX
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                {errors.uploadedCourses && (
                  <FormHelperText error>{errors.uploadedCourses}</FormHelperText>
                )}

                {uploadedCourses.map((val, index) => {
                  return (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        background: '#F5FFF1',
                        borderRadius: 2,
                      }}
                      className="p-2 mt-2"
                      key={index}
                    >
                      <Box>{val.name}</Box>
                      <Box
                        sx={{ cursor: 'pointer', color: 'red' }}
                        onClick={() => handleDeleteItem(index)}
                      >
                        <DeleteIcon />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}
        </>
      )}
      {stepperCount === 2 && (
        <Box sx={{ width: '40%', margin: 'auto' }} className="p-3">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                color: '#110B2C',
                fontSize: 16,
                fontWeight: 500,
                // marginBottom: "-12px",
              }}
            >
              Upload Questionnaire
            </Box>

            <a
              href="/assets/questionSheet.xlsx"
              download // Trigger download
              style={{
                fontSize: '16px',
                fontWeight: 'bold',
                color: 'blue',
                cursor: 'pointer',
                textDecoration: 'none',
              }}
            >
              Download template
            </a>
          </Box>
          <Box
            sx={{
              height: '200px',
              background: '#F5FFF1',
              border: '1px solid #9F9DAA',
              borderStyle: 'dashed',
              cursor: 'pointer',
              borderRadius: 2,
            }}
            className="mt-3 d-flex justify-content-center align-items-center"
            onClick={(e) => fileInputRef.current.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={(e) => handleQuestionnaireUpload(e.target.files[0])}
            />
            <Box>
              <Box>
                <img
                  src={uploadImg}
                  alt="upload-img"
                  style={{ margin: 'auto', display: 'block' }}
                />
                <Box
                  sx={{
                    fontSize: 16,
                    color: '#110B2C',
                    fontWeight: 600,
                  }}
                  className="mt-3 mx-2"
                >
                  Drag & drop files or <u style={{ color: '#2800B1' }}>Browse</u>
                </Box>
                <Typography
                  sx={{ fontSize: 12, color: '#676767', fontWeight: 400 }}
                  className="mt-3"
                >
                  Supported formats: xlsx
                </Typography>
              </Box>
            </Box>
          </Box>
          {errors.questionnaire && <FormHelperText error>{errors.questionnaire}</FormHelperText>}
          {questionnaire.map((val, index) => {
            return (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  background: '#F5FFF1',
                  borderRadius: 2,
                }}
                key={index}
                className="p-2 mt-2"
              >
                <Box>{val.name}</Box>
                <Box
                  sx={{ cursor: 'pointer', color: 'red' }}
                  onClick={() => handleDeleteQuestionnaire(index)}
                >
                  <DeleteIcon />
                </Box>
              </Box>
            );
          })}

          {/* Additional Language Question Files */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 3,
              marginBottom: 1,
            }}
          >
            <Box sx={{ fontSize: 16, fontWeight: 600, color: '#110B2C' }}>
              Additional Language Questions (Optional)
            </Box>
            <Button
              variant="contained"
              size="small"
              onClick={handleAddLanguageQuestion}
              sx={{ background: '#2800B1' }}
            >
              + Add Language Question
            </Button>
          </Box>

          {languageQuestions.map((lq, index) => (
            <Box
              key={index}
              sx={{
                background: '#F5FFF1',
                borderRadius: 2,
                padding: 2,
                marginBottom: 2,
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={4}>
                  <Select
                    size="small"
                    fullWidth
                    value={lq.languageCode}
                    onChange={(e) =>
                      handleLanguageQuestionChange(index, 'languageCode', e.target.value)
                    }
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Select Language
                    </MenuItem>
                    {getAvailableLanguagesForQuestion(index).map((lang) => (
                      <MenuItem key={lang.code} value={lang.code}>
                        {lang.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    size="small"
                    sx={{ textTransform: 'none' }}
                  >
                    {lq.file ? lq.file.name : 'Upload Question File (.xlsx)'}
                    <input
                      type="file"
                      hidden
                      accept=".xlsx,.xls"
                      onChange={(e) =>
                        handleLanguageQuestionChange(index, 'file', e.target.files[0])
                      }
                    />
                  </Button>
                </Grid>
                <Grid item xs={2}>
                  <Button
                    variant="text"
                    color="error"
                    onClick={() => handleRemoveLanguageQuestion(index)}
                  >
                    Remove
                  </Button>
                </Grid>
              </Grid>
              {errors[`languageQuestion_${index}`] && (
                <FormHelperText error>{errors[`languageQuestion_${index}`]}</FormHelperText>
              )}
            </Box>
          ))}

          {languageQuestions.length === 0 && (
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ textAlign: 'center', padding: 1 }}
            >
              No additional language questions added. Click "+ Add Language Question" to add
              questions in other languages.
            </Typography>
          )}
        </Box>
      )}
      <Box
        sx={{
          width: '40%',
          margin: 'auto',
          // position: "absolute",
          bottom: 3,
          right: '24%',
        }}
      >
        <Grid container spacing={1} columns={16} className="mt-3">
          {stepperCount > 1 && (
            <Grid item xs={8}>
              <Button
                size="medium"
                sx={{
                  borderRadius: 50,
                  fontSize: 12,
                  border: '1px solid #2800B1',
                  width: '100%',
                  color: '#706C84',
                }}
                onClick={handleOnClickPrevious}
              >
                Previous
              </Button>
            </Grid>
          )}
          <Grid item xs={8}>
            {stepperCount === 2 ? (
              <Button
                variant="contained"
                size="medium"
                sx={{
                  borderRadius: 50,
                  background: '#2800B1',
                  fontSize: 12,
                  border: '1px solid #2800B1',
                  width: '100%',
                }}
                onClick={handleSumbitUploadDetails}
                disabled={questionnaire.length === 0}
              >
                {isLoading ? <CircularProgress size={23} color="inherit" /> : 'Submit'}
              </Button>
            ) : (
              <Button
                variant="contained"
                size="medium"
                sx={{
                  borderRadius: 50,
                  background: '#2800B1',
                  fontSize: 12,
                  border: '1px solid #2800B1',
                  width: '100%',
                }}
                onClick={() => handleOnClickNext(stepperCount)}
              >
                Next
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>
    </Layout>
  );
};

export default UploadCourse;
